import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    FormHelperText,
    TextField,
} from '@mui/material';
import { AxiosError } from 'axios';
import { FormNumberField, FormSelect, FormTextField } from 'components/forms';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'routing';
import { TIER_LIST } from 'shared/constants';
import { useSendTierChangeNeedsApprovalEmail } from 'shared/hooks';
import UseDynamicUrl from 'shared/hooks/useDynamicUrl';
import { useDistributor } from 'shared/providers';
import { useHttp } from 'shared/providers/provide-http';
import { createTableDataContext } from 'shared/providers/provideTableData';
import { DynamicEmailData } from 'shared/services/email-address-service';
import { useAuthState } from 'shared/stores';
import {
    CustomerTierOverrideData,
    CustomerTierOverrideRequest,
    NeedsApprovalTierChangeEmailData,
    TableDataResponse,
} from 'types/backend';
import { CustomerTierOverrideForm } from 'types/forms';

export const tierOverrideContext = createTableDataContext<CustomerTierOverrideData>();
export function TierSetUpResolver() {
    const http = useHttp();
    const { enqueueSnackbar } = useSnackbar();
    const [error, setError] = useState<string>();
    const history = useHistory();
    const { path: managingPricesPath } = UseDynamicUrl({ route: ROUTES.managePricing });
    const authState = useAuthState();
    const currentUser = authState.user.get()?.displayName;
    const url = window.location.origin;

    const distributor = useDistributor();

    const dynamicEmailData: Omit<DynamicEmailData, 'currentUserName' | 'currentUserEmailAddress'> = {
        territorySalesLeaderName: distributor.matchingCustomer?.territorySalesLeader || '',
        territorySalesLeaderEmail: distributor.matchingCustomer?.territorySalesLeaderEmail || '',
        territorySalesRepName: distributor.matchingCustomer?.territorySalesRep || '',
        territorySalesRepEmail: distributor.matchingCustomer?.territorySalesRepEmail || '',
    };

    const sendEmail = useSendTierChangeNeedsApprovalEmail(`${url}/approvals/customerTierOverride`, dynamicEmailData);

    const response = useQuery<TableDataResponse<CustomerTierOverrideData>, Error>(
        ['DistributorTierOverride', distributor.matchingCustomer.customerNumber],
        async () => {
            const { data } = await http.post(
                `api/customertieroverride/subset/${distributor.matchingCustomer.customerNumber}`,
                {
                    FilterModel: {
                        items: [
                            {
                                columnField: 'approvalstatus',
                                operatorValue: 'equals',
                                value: 'Needs Approval',
                            },
                        ],
                    },
                    SortModel: [
                        {
                            field: 'effectivestart',
                            sort: 'desc',
                        },
                    ],
                    page: 0,
                    pageSize: 1,
                }
            );
            return data || [];
        },
        {
            onError: (error) => enqueueSnackbar(error.message, { variant: 'error' }),
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<CustomerTierOverrideForm>({
        defaultValues: {
            estAnnualLbs: '',
            currentTier: distributor.matchingCustomer?.priceTier,
            revisedTier: '',
            comments: '',
            revisionReason: '',
        },
    });

    const submitTierOverrideRequest = handleSubmit(async (form: CustomerTierOverrideForm) => {
        const requestData: CustomerTierOverrideRequest = {
            approvalStatus: 'Needs Approval',
            comments: form.comments,
            distributorId: Number(distributor.matchingCustomer?.id) ?? 0,
            accountnumber: distributor.matchingCustomer.customerNumber,
            revisionReason: form.revisionReason,
            tierOverride: form.revisedTier,
            ttmPounds: Number(form.estAnnualLbs),
        };
        const tierChangeEmailData: NeedsApprovalTierChangeEmailData = {
            customerName: distributor.matchingCustomer?.customerName ?? 'There was a problem getting customer name',
            approver: 'Clay Tiedeman',
            createdBy: currentUser ?? 'There was a problem getting the user',
            customerAccount: distributor.matchingCustomer?.customerNumber ?? 'There was a problem getting the account',
            territorySalesLeader: distributor.matchingCustomer?.territorySalesLeader ?? '',
            territorySalesRep: distributor.matchingCustomer?.territorySalesRep ?? '',
            revisedTier: form.revisedTier,
            currentTier: form.currentTier,
            estAnnualVolume: form.estAnnualLbs,
            tierRevisionReason: form.revisionReason,
            requestorComments: form.comments,
        };

        try {
            await http.post('/api/customerTierOverride/Add', requestData);
            enqueueSnackbar('Override Request Sent', { variant: 'success' });
            try {
                await sendEmail.mutateAsync(tierChangeEmailData);
            } catch (e) {
                enqueueSnackbar('Tier Override email failed to send', { variant: 'error' });
            }
            history.push(managingPricesPath);
        } catch (error) {
            const axiosErrorResponse = (error as AxiosError).response;
            const errorText =
                axiosErrorResponse?.status === 400 && axiosErrorResponse?.data
                    ? (axiosErrorResponse?.data as string)
                    : `${axiosErrorResponse?.statusText} - ${axiosErrorResponse?.status}`;

            setError(errorText);
            enqueueSnackbar('Override request failed', { variant: 'error' });
        }
    });

    return (
        <Card>
            <CardHeader title='Tier Setup' />
            <CardContent>
                <Box sx={{ padding: '10px' }}>
                    <FormNumberField
                        name='estAnnualLbs'
                        label='Annual Lbs Estimate'
                        control={control}
                        rules={{ required: 'Annual Lbs Estimate is required' }}
                        fieldError={errors.estAnnualLbs}
                    />
                </Box>
                <Box sx={{ padding: '10px' }}>
                    <FormTextField name='currentTier' label='Current Tier' control={control} disabled />
                </Box>
                <Box sx={{ padding: '10px' }}>
                    <TextField
                        sx={{ width: 1 }}
                        disabled={true}
                        label={'Pending Tier'}
                        value={response.data?.rows[0]?.tierOverride || 'N/A'}
                        InputProps={{
                            endAdornment: response.isLoading ? <CircularProgress /> : null,
                        }}
                    />
                </Box>
                <Box sx={{ padding: '10px' }}>
                    <FormSelect
                        name='revisedTier'
                        label='Revised Tier'
                        control={control}
                        rules={{ required: 'Revised Tier is required' }}
                        options={TIER_LIST}
                    />
                    <FormHelperText error>{errors.revisedTier?.message}</FormHelperText>
                </Box>
                <Box sx={{ padding: '10px' }}>
                    <FormSelect
                        name='revisionReason'
                        label='Revision Reason'
                        control={control}
                        rules={{ required: 'Revision Reason is required' }}
                        options={[
                            { value: 'Volume Growth', displayText: 'Volume Growth' },
                            { value: 'Competition', displayText: 'Competition' },
                            { value: 'Other', displayText: 'Other' },
                        ]}
                    />
                    <FormHelperText error>{errors.revisionReason?.message}</FormHelperText>
                </Box>
                <Box sx={{ padding: '10px' }}>
                    <FormTextField
                        name='comments'
                        label='Comments'
                        control={control}
                        rules={{ required: 'Comments are required' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '10px' }}>
                    <Button variant='contained' onClick={submitTierOverrideRequest}>
                        Save
                    </Button>
                </Box>
                {error && <Alert severity='error'>{error}</Alert>}
            </CardContent>
        </Card>
    );
}
